/**
 * PlanAheadProjectionsTab — the outlook surface for /plan-ahead.
 *
 * Phase 85.4. This file used to be fiction end to end: a hardcoded five-year
 * series from ₹3.2L to ₹6.84L, ₹25.6L of "current liabilities" projected to
 * ₹18.2L, a factor table of ₹48,500/mo and ₹31,800/mo, and an assumption line
 * reading "Monthly Building Money ₹48,500 — based on your recent savings and
 * contribution behavior". None of it came from the user. It rendered for
 * everyone, including a real user with no accounts, no transactions and no
 * uploads, on 2026-08-26.
 *
 * Everything here now comes from `GET /api/projections/current`, whose numbers
 * are derived from the person's own snapshots, holdings and liabilities, plus
 * `GET /api/position/history` for what actually happened. Where the inputs are
 * too thin to project honestly, this tab says that instead of drawing a line.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ComposedChart, Area, Line, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { formatMoney } from '@/lib/format';

const PROJECTION_YEARS = 5;

function Section({ children, className = '' }) {
  return <div className={`bg-card rounded-xl border border-border p-5 lg:p-6 shadow-sm ${className}`}>{children}</div>;
}

function ChartTooltip({ active, payload, label, baseCurrency }) {
  if (!active || !payload?.length) return null;
  const hist = payload.find((p) => p.dataKey === 'historical');
  const proj = payload.find((p) => p.dataKey === 'projected');
  const v = (hist?.value ?? proj?.value ?? 0);
  const kind = hist?.value != null ? 'Recorded' : 'Projected';
  return (
    <div className="bg-white border border-border rounded-lg px-3 py-2 shadow-sm">
      <p className="text-[11px] text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-charcoal num">{formatMoney(v, baseCurrency)}</p>
      <p className="text-[10px] text-muted-foreground">{kind}</p>
    </div>
  );
}

/** Nothing to project from, and we say why rather than drawing something. */
function NotEnoughYet({ reason, detail }) {
  return (
    <Section data-testid="plan-projections-empty">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-3">Your Projection Briefing</p>
      <p className="font-fraunces text-lg font-medium text-charcoal mb-2" data-testid="plan-projections-empty-headline">{reason}</p>
      <p className="text-sm text-charcoal/70 leading-relaxed">{detail}</p>
    </Section>
  );
}

export default function PlanAheadProjectionsTab({ baseCurrency = 'INR' }) {
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [projection, setProjection] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) { if (!cancelled) { setLoading(false); setFailed(true); } return; }
        const headers = { Authorization: `Bearer ${token}` };
        const api = process.env.REACT_APP_BACKEND_URL;
        const [p, h] = await Promise.all([
          fetch(`${api}/api/projections/current`, { headers }),
          fetch(`${api}/api/position/history?days=400`, { headers }),
        ]);
        if (cancelled) return;
        if (p.ok) setProjection(await p.json()); else setFailed(true);
        if (h.ok) setHistory(await h.json());
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currency = projection?.currency || baseCurrency;
  const inputs = projection?.inputs || {};
  const outputs = projection?.outputs || {};
  const assumptions = projection?.assumptions || {};
  const series = Array.isArray(outputs.series_yearly) ? outputs.series_yearly : [];

  // Recorded snapshots only. A trend needs three points; two is a line we have
  // no right to draw.
  const points = useMemo(() => (
    (history?.points || []).map((pt) => ({
      month: pt.date, historical: pt.net_worth, projected: null,
    }))
  ), [history]);

  const projectedRows = useMemo(() => (
    series
      .filter((s) => s.year > 0 && s.year <= PROJECTION_YEARS)
      .map((s) => ({ month: `+${s.year}y`, historical: null, projected: s.net_worth }))
  ), [series]);

  const nowPoint = series.find((s) => s.year === 0);
  const data = useMemo(() => {
    if (!nowPoint) return [];
    const now = { month: 'Now', historical: nowPoint.net_worth, projected: nowPoint.net_worth };
    return [...points, now, ...projectedRows];
  }, [points, projectedRows, nowPoint]);

  const currentNw = nowPoint?.net_worth ?? null;
  const horizon = series.find((s) => s.year === PROJECTION_YEARS);
  const projectedNw = horizon?.net_worth ?? null;

  const currentLiabilities = inputs.current_liabilities ?? nowPoint?.liabilities ?? null;
  const projectedLiabilities = horizon?.liabilities ?? null;
  const monthsOfData = Number(inputs.months_of_data || 0);
  const hasPosition = Number(inputs.current_assets || 0) > 0
    || Number(inputs.current_liabilities || 0) > 0;

  if (loading) {
    return <Section><p className="text-sm text-muted-foreground py-6" data-testid="plan-projections-loading">Loading your projection…</p></Section>;
  }
  if (failed) {
    return <NotEnoughYet
      reason="We couldn't load your projection."
      detail="Nothing is being estimated in the meantime. Refresh, and if it keeps failing, tell us — we would rather show you nothing than a number we cannot stand behind."
    />;
  }
  if (!hasPosition || !nowPoint || !series.length) {
    return <NotEnoughYet
      reason="There is nothing to project from yet."
      detail="Projections are built from your own accounts, holdings and liabilities. Add an account or upload a statement, and this becomes a projection of your money rather than an illustration of somebody else's."
    />;
  }

  const factors = [
    currentLiabilities != null && {
      label: 'Liabilities', value: formatMoney(currentLiabilities, currency),
      impact: projectedLiabilities != null && projectedLiabilities < currentLiabilities
        ? 'Decreasing at your current repayment pace' : 'From your recorded liabilities',
      color: '#3D7A63', dir: projectedLiabilities != null && projectedLiabilities < currentLiabilities ? '↓' : '=',
    },
    inputs.avg_monthly_surplus != null && {
      label: 'Monthly surplus', value: `${formatMoney(inputs.avg_monthly_surplus, currency)}/mo`,
      impact: monthsOfData > 0
        ? `Averaged from ${monthsOfData} month${monthsOfData === 1 ? '' : 's'} of your recorded data`
        : 'From your onboarding figures',
      color: '#3D7A63', dir: Number(inputs.avg_monthly_surplus) >= 0 ? '+' : '−',
    },
    inputs.current_assets != null && {
      label: 'Assets today', value: formatMoney(inputs.current_assets, currency),
      impact: 'The starting point for every projected year', color: '#163A32', dir: '=',
    },
  ].filter(Boolean);

  const assumptionRows = [
    assumptions.income_growth_pct != null && { label: 'Income growth', value: `${assumptions.income_growth_pct}% annually`, note: 'Used to estimate future income and available savings.' },
    assumptions.inflation_pct != null && { label: 'Inflation', value: `${assumptions.inflation_pct}% annually`, note: 'Applied to estimate real purchasing power over time.' },
    assumptions.nominal_return_pct != null && { label: 'Investment return', value: `${assumptions.nominal_return_pct}% annually`, note: 'Used only to estimate future investment values. Actual returns may vary.' },
    assumptions.monthly_surplus != null && { label: 'Monthly surplus used', value: formatMoney(assumptions.monthly_surplus, currency), note: monthsOfData > 0 ? `Derived from ${monthsOfData} month${monthsOfData === 1 ? '' : 's'} of your own recorded data.` : 'Derived from the figures you entered during onboarding — not from observed behaviour yet.' },
    { label: 'Liability repayment pace', value: 'Current EMI schedule', note: 'Assumes no prepayments beyond current EMIs.' },
    { label: 'Projection period', value: `${PROJECTION_YEARS} years`, note: 'Projections beyond 5 years carry greater uncertainty.' },
  ].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="plan-ahead-projections">
      {/* Projection Briefing */}
      <Section className="border-l-[3px] border-l-[#163A32]">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold mb-3">Your Projection Briefing</p>
        <p className="font-fraunces text-lg font-medium text-charcoal mb-2" data-testid="plan-projection-headline">
          {projectedNw != null && currentNw != null && projectedNw > currentNw
            ? `On these assumptions, your position strengthens over the next ${PROJECTION_YEARS} years.`
            : `Here is where these assumptions lead over the next ${PROJECTION_YEARS} years.`}
        </p>
        {currentNw != null && projectedNw != null && (
          <p className="text-sm text-charcoal/70 leading-relaxed mb-2">
            From <strong className="text-charcoal">{formatMoney(currentNw, currency)}</strong> today to{' '}
            <strong className="text-charcoal">{formatMoney(projectedNw, currency)}</strong> — an estimate, not a forecast.
          </p>
        )}
        <p className="text-sm text-charcoal/60 mb-4" data-testid="plan-projection-basis">
          {monthsOfData > 0
            ? `Built from ${monthsOfData} month${monthsOfData === 1 ? '' : 's'} of your recorded data and the assumptions below.`
            : 'Built from your current balances and the assumptions below — there is not enough recorded history yet to observe your saving behaviour.'}
        </p>
        <button onClick={() => setAssumptionsOpen(true)} className="px-4 py-2 rounded-lg text-sm font-medium border border-evergreen/30 text-evergreen hover:bg-evergreen/5 transition-colors" data-testid="plan-review-assumptions">
          Review assumptions
        </button>
      </Section>

      {/* Chart */}
      <Section>
        <div className="flex items-start justify-between gap-4 mb-2">
          <p className="text-xs font-semibold text-charcoal">Projected Net Worth</p>
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
            {points.length > 0 && (
              <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0.5 bg-charcoal rounded-full" /> Recorded</span>
            )}
            <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-px border-t-2 border-dashed" style={{ borderColor: '#3D7A63' }} /> Projected</span>
          </div>
        </div>
        {currentNw != null && projectedNw != null && (
          <p className="text-[11px] text-muted-foreground/70 mb-5">
            Now: {formatMoney(currentNw, currency)} → in {PROJECTION_YEARS} years: {formatMoney(projectedNw, currency)}
          </p>
        )}

        <div style={{ height: 240, width: '100%', minHeight: 240 }} data-testid="plan-projection-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <ComposedChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="planHistGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#163A32" stopOpacity={0.10} />
                  <stop offset="95%" stopColor="#163A32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.6} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip baseCurrency={currency} />} />
              <ReferenceLine x="Now" stroke="#D5D3CE" strokeDasharray="3 2" label={{ value: 'Now', position: 'top', fontSize: 10, fill: '#9B9890' }} />
              <Area type="monotone" dataKey="historical" stroke="#163A32" strokeWidth={1.8} fill="url(#planHistGrad)" dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="projected" stroke="#3D7A63" strokeWidth={1.8} strokeDasharray="5 4" dot={false} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground/60 mt-3 italic" data-testid="plan-chart-note">
          {points.length >= 3
            ? 'Solid line is your recorded position. Dashed line is projected from the assumptions below.'
            : 'No recorded history to draw yet — we start plotting your position from your first snapshot. The dashed line is projected from the assumptions below.'}
        </p>
      </Section>

      {/* Factors */}
      <Section>
        <p className="text-xs font-semibold text-charcoal mb-5">What&apos;s Shaping Your Outlook</p>
        <div className="space-y-4" data-testid="plan-projection-factors">
          {factors.map((f) => (
            <div key={f.label} className="flex items-center gap-4">
              <span className="w-5 h-5 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0" style={{ backgroundColor: f.color + '18', color: f.color }}>{f.dir}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal">{f.label}</p>
                <p className="text-xs text-muted-foreground/80">{f.impact}</p>
              </div>
              <p className="num text-sm font-semibold text-charcoal shrink-0">{f.value}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Liability outlook — only when there are liabilities */}
      {currentLiabilities != null && Number(currentLiabilities) > 0 && (
        <Section>
          <p className="text-xs font-semibold text-charcoal mb-4">Liability Outlook</p>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium mb-1">Current liabilities</p>
              <p className="font-fraunces text-2xl font-medium num" style={{ color: '#A84E38' }} data-testid="plan-liabilities-current">{formatMoney(currentLiabilities, currency)}</p>
            </div>
            {projectedLiabilities != null && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-medium mb-1">Projected in {PROJECTION_YEARS} years</p>
                <p className="font-fraunces text-2xl font-medium text-charcoal num" data-testid="plan-liabilities-projected">{formatMoney(projectedLiabilities, currency)}</p>
              </div>
            )}
          </div>
          {outputs.debt_free_date && (
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed" data-testid="plan-debt-free">
              On the current EMI schedule, the last scheduled payment falls in {String(outputs.debt_free_date).slice(0, 7)}.
            </p>
          )}
          {projectedLiabilities != null && Number(currentLiabilities) > 0 && (
            <div className="mt-4 h-1.5 bg-border/40 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, Math.max(0, (1 - projectedLiabilities / currentLiabilities) * 100))}%`,
                  backgroundColor: '#A07830',
                }}
              />
            </div>
          )}
        </Section>
      )}

      {/* Assumptions (collapsible) */}
      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm" data-testid="plan-assumptions">
        <button onClick={() => setAssumptionsOpen(!assumptionsOpen)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-offwhite/30 transition-colors">
          <div>
            <p className="text-xs font-semibold text-charcoal text-left">Projection Assumptions</p>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5 text-left">The data and assumptions shaping your projections.</p>
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${assumptionsOpen ? 'rotate-180' : ''}`} />
        </button>
        {assumptionsOpen && (
          <div className="px-5 pb-5 border-t border-border/60 divide-y divide-border/40">
            {assumptionRows.map((a) => (
              <div key={a.label} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">{a.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{a.note}</p>
                </div>
                <p className="text-sm text-charcoal font-medium shrink-0 num">{a.value}</p>
              </div>
            ))}
            {projection?.computed_at && (
              <div className="py-3">
                <p className="text-[11px] text-muted-foreground">Computed {new Date(projection.computed_at).toLocaleDateString()}.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
